import './applicantResidenceTypePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { ResidenceTypeEnum } from '../../../store/applicantStore/enums/ResidenceTypeEnum';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCurrentResidencePersisted },
    currentResidenceType,
    isSyncingResidence,
    syncingResidenceFailedMessage,
    setCurrentResidenceType,
    syncResidence,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      residenceTypeValue: currentResidenceType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<ResidenceTypeEnum>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const residenceTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(currentResidenceType) && isValidClickEvent(event),
    );

    setCurrentResidenceType(residenceTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { residenceTypeValue } = values;

    if (!residenceTypeValue) {
      return;
    }

    if (isCurrentResidencePersisted && isDirty) {
      const synced = await syncResidence();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-residence-type-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantResidenceType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantResidenceType.form.h1}>
                {t('ApplicantResidenceTypePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantResidenceType.form.h2}>
                {t('ApplicantResidenceTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantResidenceType.alert.error
                    }
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="residenceTypeValue"
                  options={[
                    {
                      label: t('ApplicantResidenceTypePage.own'),
                      value: ResidenceTypeEnum.OWN,
                      testId:
                        locator.pages.applicantResidenceType.form.radioOption1,
                    },
                    {
                      label: t('ApplicantResidenceTypePage.rent'),
                      value: ResidenceTypeEnum.RENT,
                      testId:
                        locator.pages.applicantResidenceType.form.radioOption2,
                    },
                    {
                      label: t('ApplicantResidenceTypePage.other'),
                      value: ResidenceTypeEnum.OTHER,
                      testId:
                        locator.pages.applicantResidenceType.form.radioOption3,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantResidenceType.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantResidenceType.form.submitButton
                  }
                  disabled={isEmpty(currentResidenceType)}
                  loader={isSyncingResidence}
                  fullWidth
                  submit
                >
                  {t('ApplicantResidenceTypePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantResidenceType.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantResidenceTypePage';
